import { useCallback, useMemo } from "react";
import {
  Alert,
  Button,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowsExchange,
  IconReceipt,
} from "@tabler/icons-react";

import { useCasePrefixReassignment } from "../../hooks/useCasePrefixReassignment";
import {
  type CasePrefixReassignmentContext,
  getCasePrefixReassignmentError,
  type ReassignCasePrefixResponse,
} from "../../types/casePrefixReassignment";

interface CasePrefixReassignmentModalProps {
  opened: boolean;
  onClose: () => void;
  caseId: string;
  onReassigned: (response: ReassignCasePrefixResponse) => void;
}

interface PrefixReassignmentFormValues {
  targetPrefixId: string;
  caseNumberRemainder: string;
  reason: string;
}

const initialValues: PrefixReassignmentFormValues = {
  targetPrefixId: "",
  caseNumberRemainder: "",
  reason: "",
};

const MAX_CANONICAL_CASE_NUMBER_LENGTH = 10;

function validateTargetPrefix(value: string): string | null {
  return value ? null : "Choose a new prefix.";
}

function validateCanonicalRemainder(
  value: string,
  values: PrefixReassignmentFormValues,
  context?: CasePrefixReassignmentContext
): string | null {
  const prefix = context?.availablePrefixes.find(
    (option) => option.id === values.targetPrefixId
  );
  if (!prefix?.requiresCaseNumberRemainder || prefix.isIdentityRepair)
    return null;
  if (!value.trim()) return "Enter the case number after the fixed prefix.";
  if (
    prefix.code.length + value.trim().length >
    MAX_CANONICAL_CASE_NUMBER_LENGTH
  ) {
    return `The full case number must be ${MAX_CANONICAL_CASE_NUMBER_LENGTH} characters or fewer.`;
  }
  return null;
}

function validateReason(value: string): string | null {
  const length = value.trim().length;
  if (length < 3) return "Enter at least 3 characters.";
  if (length > 500) return "Enter no more than 500 characters.";
  return null;
}

export default function CasePrefixReassignmentModal({
  opened,
  onClose,
  caseId,
  onReassigned,
}: CasePrefixReassignmentModalProps) {
  const { canReassignPrefix, contextQuery, reassignMutation } =
    useCasePrefixReassignment(caseId, { enabled: opened });
  const context = contextQuery.data;
  const validateCaseNumberRemainder = useCallback(
    (value: string, values: PrefixReassignmentFormValues) =>
      validateCanonicalRemainder(value, values, context),
    [context]
  );
  const form = useForm<PrefixReassignmentFormValues>({
    initialValues,
    validateInputOnBlur: true,
    validate: {
      targetPrefixId: validateTargetPrefix,
      caseNumberRemainder: validateCaseNumberRemainder,
      reason: validateReason,
    },
  });
  const prefixOptions = useMemo(
    () =>
      (context?.availablePrefixes ?? []).map((prefix) => ({
        value: prefix.id,
        label: prefix.isIdentityRepair
          ? `${prefix.code} (repair number to ${prefix.repairCaseNumber})`
          : prefix.code,
      })),
    [context?.availablePrefixes]
  );
  const selectedPrefix = useMemo(
    () =>
      context?.availablePrefixes.find(
        (prefix) => prefix.id === form.values.targetPrefixId
      ),
    [context?.availablePrefixes, form.values.targetPrefixId]
  );
  const canonicalCaseNumber = useMemo(
    () =>
      selectedPrefix?.isIdentityRepair
        ? selectedPrefix.repairCaseNumber ?? ""
        : selectedPrefix?.requiresCaseNumberRemainder
        ? `${selectedPrefix.code}${form.values.caseNumberRemainder
            .trim()
            .toUpperCase()}`
        : "",
    [form.values.caseNumberRemainder, selectedPrefix]
  );
  const maximumRemainderLength = useMemo(
    () =>
      selectedPrefix
        ? MAX_CANONICAL_CASE_NUMBER_LENGTH - selectedPrefix.code.length
        : MAX_CANONICAL_CASE_NUMBER_LENGTH,
    [selectedPrefix]
  );
  const billingChangeNotice = useMemo(() => {
    if (!selectedPrefix) return null;
    if (!selectedPrefix.defaultBillingScenarioName) {
      return `${selectedPrefix.code} has no default billing scenario configured, so this case keeps the billing scenario it has now.`;
    }
    return `This case will bill as ${selectedPrefix.defaultBillingScenarioName}, the default for ${selectedPrefix.code}. Any scenario it holds now is replaced.`;
  }, [selectedPrefix]);
  const mutationError = useMemo(() => {
    const knownError = getCasePrefixReassignmentError(reassignMutation.error);
    return (
      knownError?.message ?? "The case prefix could not be changed. Try again."
    );
  }, [reassignMutation.error]);
  const canSubmit = useMemo(() => {
    const remainder = form.values.caseNumberRemainder.trim();
    const hasValidCanonicalNumber =
      !selectedPrefix?.requiresCaseNumberRemainder ||
      Boolean(selectedPrefix?.isIdentityRepair) ||
      (remainder.length > 0 &&
        selectedPrefix.code.length + remainder.length <=
          MAX_CANONICAL_CASE_NUMBER_LENGTH);
    return Boolean(
      context?.canReassign &&
        selectedPrefix &&
        hasValidCanonicalNumber &&
        form.values.reason.trim().length >= 3 &&
        !reassignMutation.isPending
    );
  }, [
    context?.canReassign,
    form.values.caseNumberRemainder,
    form.values.reason,
    reassignMutation.isPending,
    selectedPrefix,
  ]);

  const resetAndClose = useCallback(() => {
    form.reset();
    reassignMutation.reset();
    onClose();
  }, [form, onClose, reassignMutation]);

  const handleTargetPrefixChange = useCallback(
    (targetPrefixId: string | null) => {
      if (!targetPrefixId || targetPrefixId === form.values.targetPrefixId)
        return;
      form.setFieldValue("targetPrefixId", targetPrefixId);
      form.setFieldValue("caseNumberRemainder", "");
    },
    [form]
  );

  const handleSubmit = useCallback(
    async (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validation = form.validate();
      if (
        validation.hasErrors ||
        !context ||
        !selectedPrefix ||
        !canSubmit ||
        reassignMutation.isPending
      ) {
        return;
      }

      try {
        const response = await reassignMutation.mutateAsync({
          targetPrefixId: selectedPrefix.id,
          ...(selectedPrefix.requiresCaseNumberRemainder
            ? {
                caseNumberRemainder: form.values.caseNumberRemainder
                  .trim()
                  .toUpperCase(),
              }
            : {}),
          expectedCurrentCaseNumber: context.currentIdentity.caseNumber,
          expectedCurrentPrefixId: context.currentIdentity.prefix.id,
          reason: form.values.reason.trim(),
        });
        onReassigned(response);
        resetAndClose();
      } catch {
        // The mutation renders an actionable error and keeps the form open.
      }
    },
    [
      canSubmit,
      context,
      form,
      onReassigned,
      reassignMutation,
      resetAndClose,
      selectedPrefix,
    ]
  );

  return (
    <Modal
      opened={opened}
      onClose={resetAndClose}
      closeOnClickOutside={false}
      closeOnEscape={!reassignMutation.isPending}
      closeButtonProps={{ disabled: reassignMutation.isPending }}
      title="Change case prefix"
      size="lg"
      centered
      styles={{
        content: {
          maxWidth: "calc(100vw - var(--mantine-spacing-md) * 2)",
          maxHeight: "calc(100dvh - var(--mantine-spacing-md) * 2)",
        },
        body: { overflowY: "auto" },
      }}
    >
      {contextQuery.isLoading ? (
        <Group justify="center" py="xl">
          <Loader aria-label="Loading prefix reassignment details" />
        </Group>
      ) : contextQuery.isError ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="Unable to load case prefix details"
          role="alert"
        >
          Close this window and try again.
        </Alert>
      ) : !canReassignPrefix ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="Permission required"
          role="alert"
        >
          You do not have permission to change case prefixes.
        </Alert>
      ) : context && !context.canReassign ? (
        <Alert
          icon={<IconAlertCircle size={18} />}
          color="red"
          title="This case cannot be changed safely"
          role="alert"
        >
          {context.blockedReason ||
            "No replacement prefixes are currently available."}
        </Alert>
      ) : context ? (
        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <Stack gap={2}>
              <Text size="sm" c="dimmed">
                {context.lifecycleStatus}
              </Text>
              <Text fw={600}>
                Current number: {context.currentIdentity.caseNumber}
              </Text>
            </Stack>

            <Select
              label="New prefix"
              description="Choose the prefix that should identify this case."
              placeholder="Choose a prefix"
              data={prefixOptions}
              searchable
              required
              allowDeselect={false}
              disabled={reassignMutation.isPending}
              comboboxProps={{
                position: "bottom-start",
                offset: 8,
                withinPortal: true,
              }}
              {...form.getInputProps("targetPrefixId")}
              onChange={handleTargetPrefixChange}
            />

            {selectedPrefix?.requiresCaseNumberRemainder &&
              !selectedPrefix.isIdentityRepair && (
                <TextInput
                  label="New case number"
                  description={`Prefix ${selectedPrefix.code} is fixed. Enter the remaining characters.`}
                  placeholder="26-12345"
                  leftSection={selectedPrefix.code}
                  leftSectionWidth={50}
                  required
                  maxLength={maximumRemainderLength}
                  disabled={reassignMutation.isPending}
                  {...form.getInputProps("caseNumberRemainder")}
                />
              )}

            <Textarea
              label="Reason"
              description="This explanation is stored in the permanent correction history."
              placeholder="Explain why the original prefix was incorrect"
              required
              minRows={3}
              maxLength={500}
              disabled={reassignMutation.isPending}
              {...form.getInputProps("reason")}
            />

            {selectedPrefix?.isIdentityRepair ? (
              <Alert
                icon={<IconArrowsExchange size={18} />}
                color="blue"
                title={`Repair the case number to ${canonicalCaseNumber}`}
              >
                The stored external canonical number is already validated for
                this case. This repairs the number without consuming a new
                sequence or changing billing.
              </Alert>
            ) : selectedPrefix?.requiresCaseNumberRemainder ? (
              <Alert
                icon={<IconArrowsExchange size={18} />}
                color="blue"
                title={
                  canonicalCaseNumber
                    ? `The new case number will be ${canonicalCaseNumber}`
                    : `Enter the number after ${selectedPrefix.code}`
                }
              >
                The full case number must be unique. The existing internal
                number will be returned to its reusable pool when applicable.
              </Alert>
            ) : selectedPrefix ? (
              <Alert
                icon={<IconArrowsExchange size={18} />}
                color="blue"
                title={`A new ${selectedPrefix.code} number will be assigned`}
              >
                The shared allocator will use the lowest safe reusable number
                first, then the next sequence number.{" "}
                {context.currentIdentity.caseNumber} will be released and, when
                eligible, returned to its reusable pool.
              </Alert>
            ) : null}

            {billingChangeNotice && (
              <Alert
                icon={<IconReceipt size={18} />}
                color="blue"
                variant="light"
                title="Billing scenario follows the new prefix"
              >
                {billingChangeNotice}
              </Alert>
            )}

            <Alert
              icon={<IconAlertTriangle size={18} />}
              color="orange"
              title="Physical labels require follow-up"
            >
              Existing cassette, slide, and paperwork labels are not changed.
              Reprint or replace them after the new number is confirmed.
              Existing workflow data is not changed.
            </Alert>

            {reassignMutation.isError && (
              <Alert
                icon={<IconAlertCircle size={18} />}
                color="red"
                title="Case prefix could not be changed"
                role="alert"
              >
                {mutationError}
              </Alert>
            )}

            <Group justify="flex-end">
              <Button
                type="button"
                variant="default"
                onClick={resetAndClose}
                disabled={reassignMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                loading={reassignMutation.isPending}
                disabled={!canSubmit}
              >
                {reassignMutation.isPending
                  ? "Saving prefix change"
                  : "Save prefix change"}
              </Button>
            </Group>
          </Stack>
        </form>
      ) : null}
    </Modal>
  );
}
